import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import teamImg from "@/assets/team.jpg";
import { Target, Lightbulb, HeartHandshake, ShieldCheck, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — BigBrandBooster Digital Marketing Agency" },
      { name: "description", content: "Meet BigBrandBooster — strategists, creatives and engineers building growth programs for ambitious brands worldwide." },
      { property: "og:title", content: "About — BigBrandBooster" },
      { property: "og:description", content: "Meet the team engineering digital growth." },
    ],
    links: [{ rel: "canonical", href: "/about" }],
  }),
  component: AboutPage,
});

const values = [
  { icon: Target, title: "Outcomes first", desc: "Every recommendation maps to a metric that matters to your business." },
  { icon: Lightbulb, title: "Creative rigor", desc: "We test boldly, learn fast, and scale only what proves itself." },
  { icon: HeartHandshake, title: "True partnership", desc: "Embedded teams that act as an extension of your in-house marketing." },
  { icon: ShieldCheck, title: "Transparent always", desc: "Open dashboards, honest reporting, and zero black boxes." },
];

function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
         gradientClass="bg-about-gradient"
        //title={<>We engineer <span className="text-accent">digital growth</span> for brands that mean it.</>}
        title={<><span className="text-accent">Digital growth </span>{" "} for brands that mean it.</>}
        description="Founded on the belief that performance and craft belong together — we partner with leaders building the next chapter of their category."
      />

      <section className="container-page py-24 grid lg:grid-cols-2 gap-16 items-center">
        <img src={teamImg} alt="Team collaboration" loading="lazy" className="rounded-3xl shadow-elegant aspect-[4/3] object-cover" />
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Our story</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-ink">Small studio with a Growth-First Mindset</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            BigBrandBooster was founded with a clear vision — marketing should create real business impact, not just generate vanity metrics. Though this is just the beginning of our journey, we are committed to helping businesses establish a strong digital presence, attract the right customers, and achieve sustainable growth.
            </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            We work closely with every client, treating their business goals as our own and focusing on strategies that drive visibility, leads, and revenue. Our mission is to build lasting relationships and earn trust through consistent execution, clear communication, and results that matter.
          </p>
          <Link to="/careers" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
            Join the team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="bg-secondary/40 py-24">
        <div className="container-page">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ink max-w-3xl">What we believe.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl bg-card border border-border p-7 hover:shadow-elegant transition">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-gradient text-primary-foreground"><v.icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-xl font-semibold text-ink">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
